import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Spinner,
  FileUpload,
  Header,
  Input,
  FormField,
} from "@amzn/awsui-components-react";
import {
  Icon
} from "@amzn/awsui-components-console";
import { capitalizeFirstLetter } from "src/Ateam-BI/utils/generalUtils";
import { TEMPLATE_APP, TEMPLATES } from "../Constants";
import { DefaultProcessModel, DefaultUploadModel, ERROR_MATCHERS, JSON_FILE_TYPE, REDIRECT_WAIT_TIME, UploadAppModalText } from "./Constants/uploadAppModal";
import { PROCESS_TYPE_CONFIG } from "./Constants/downloadAppModal";
import { addApp, createAuditLog, getAllAppsForConsolidatedSheet } from "../Utils/AppActivityUtils";
import { createLogicoProcess, createProcess as createFriscoProcess, getCurrentTime } from "../Utils/AppActivityUtilsAppsyncPlus";
import { createFlexicoTemplate } from "src/utilities/FlexicoAPIs";
import { PROCESS_KEYS as FRISCO_FLEXICO_PROCESS_KEYS } from "src/Ateam-BI/Frisco/Constants";
import { PROCESS_KEYS as LOGICO_PROCESS_KEYS } from "src/Ateam-BI/Logico/Constants";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { addSecondaryDetails, findMissingFields, getAppDetailsUrl, getSecondaryDetailData, hasDuplicateAppName, removeSecondaryDetails } from "../Utils/UploadAppUtils";
import { GlobalState, ProcessModel, UploadAppModalProps, UploadModel, AppData, AppModel, AddAppV2Response, GraphQLErrorResponse, IDResponse, CreateLogicoProcessResponse, CreateFlexicoProcessResponse, CreateFriscoProcessResponse, CreateProcess, UploadAppPreviewProps, BaseModel, UploadAppLoadingScreenProps, ConsolidatedAppData } from "./Types/uploadAppModal";
import { ProcessData } from "./Types/downloadAppModal";

const UploadAppModal = ({ 
  onCloseModal,
}: UploadAppModalProps) => {
  const history = useHistory();
  const currentStage = useSelector((globalState: GlobalState) => globalState.auth.currentStage);
  const userId = useSelector((globalState: GlobalState) => globalState.auth.userId);

  const [showModal, setShowModal] = useState(true);
  const [processingFile, setProcessingFile] = useState(false);
  const [uploadAppData, setUploadAppData] = useState<AppData>();
  const [uploadFile, setUploadFile] = useState<File>();
  const [uploadError, setUploadError] = useState<string[]>([])
  const [currentAppNames, setCurrentAppNames] = useState<Set<string>>(new Set())
  const [uploadModel, setUploadModel] = useState<UploadModel>(DefaultUploadModel);

  const loadCurrentAppNames = async () => {
    let allApps = await getAllAppsForConsolidatedSheet();
    if (allApps.length != currentAppNames.size) {
      setCurrentAppNames(
        new Set(
          allApps.map((app:ConsolidatedAppData)  => app.app_name)
        )
      )
    }
  }

  useEffect(() => {
    loadCurrentAppNames();   
  }, [])

  const processFile = async (uploadFile: File) => {
    setProcessingFile(true);

    if (uploadFile.type != JSON_FILE_TYPE) {
        setUploadError([UploadAppModalText.IncorrectFileType]);
        setProcessingFile(false);
        return;
    }

    const appDataString = await uploadFile.text();
    const appData = JSON.parse(appDataString);

    const missingFields = findMissingFields(appData);
    if (missingFields.length > 0) {
        setUploadError([UploadAppModalText.MissingFields(missingFields.join(","))]);
        setProcessingFile(false);
        return;
    }

    setUploadAppData(appData)
    setProcessingFile(false);
  }

  useEffect(() => {
    setUploadError([]);
    setUploadAppData(undefined);
    if (uploadFile) processFile(uploadFile)
  }, [uploadFile])

  const isAppLoaded = () => {
    return !!uploadModel.app.appData;
  }

  const loadUploadModel = (appData: AppData) => {
    Object.keys(appData.processes).forEach((processType: string) => {
      appData.processes[processType].forEach((process: ProcessData, index: number) => {
        const processModel: ProcessModel = structuredClone(DefaultProcessModel);
        processModel.processData = process;
        uploadModel.processes[processType][index] = processModel;
      })
    })
    delete appData["processes"]
    uploadModel.app.appData = appData;
    setUploadModel(structuredClone(uploadModel))
  }

  useEffect(() => {
    if (uploadAppData) {
      loadUploadModel(uploadAppData);
    } else {
      setUploadModel(structuredClone(DefaultUploadModel));
    }
  }, [uploadAppData])

  const handleAppSuccess = (appModel: AppModel, appId: string) => {
    appModel.creating = false;
    appModel.created = true;
    appModel.appData && (appModel.appData.appId = appId);
    uploadModel.app = appModel;
    setUploadModel(structuredClone(uploadModel))
  }

  const createApp = (appModel: AppModel): Promise<string> => {
    const appData = appModel.appData;
    if (!appData) return new Promise((resolve) => resolve(""));
    if (appData.appId) return new Promise((resolve) => resolve(appData.appId));
    appData[TEMPLATE_APP.LAST_UPDATED_BY] = userId;
    appData[TEMPLATE_APP.CREATED_BY] = userId;
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.MAPLE;
    createAuditLog(`APP`, `ADD_REQ#${getCurrentTime()}`, "", JSON.stringify({}), JSON.stringify(appData), "ADD REQ", userId)
    return new Promise((resolve, reject) => {
      addApp(appData, function onSuccess(response: AddAppV2Response) {
        handleAppSuccess(appModel, response.data.addAppV2.app_id);
        resolve(response.data.addAppV2.app_id);
      }, function onFailure(error: GraphQLErrorResponse) {
        reject(error)
      })
    })
  }

  const handleProcessError = (processModel: ProcessModel, processType: string, error: GraphQLErrorResponse, index: number) => {
    const errors = error?.errors;
    const message = errors ? errors[0]?.message : undefined;
    if (message && uploadModel.processes[processType][index]) {
      processModel.error = message;
      processModel.creating = false;
      uploadModel.processes[processType][index] = processModel;
      setUploadModel(structuredClone(uploadModel))
    }
  }

  const handleProcessSuccess = (processModel: ProcessModel, processType: string,  index: number) => {
    if (uploadModel.processes[processType][index]) {
      processModel.creating = false;
      processModel.created = true;
      uploadModel.processes[processType][index] = processModel;
      setUploadModel(structuredClone(uploadModel))
    }
  }

  const createLogico = (processModel: ProcessModel, processType: string, index: number): Promise<IDResponse | GraphQLErrorResponse>  => {
    const logicoData = removeSecondaryDetails(processModel.processData);
    logicoData[LOGICO_PROCESS_KEYS.created_by] = userId;
    return new Promise((resolve, _) => {
      createLogicoProcess(logicoData, async function onSuccess(response: CreateLogicoProcessResponse) {
        createAuditLog(`LOGICO`,`ADD_REQ#${getCurrentTime()}`, "Adding a process", JSON.stringify({}), JSON.stringify(logicoData), "ADD REQ", userId)
        if (response.data.createLogicoProcess.logico_process_id) {
          const secondaryDetailData = getSecondaryDetailData(processModel.processData, processType, response.data.createLogicoProcess.logico_process_id, response.data.createLogicoProcess.mapping_id);
          await addSecondaryDetails(secondaryDetailData, processType, userId, currentStage);
        }
        handleProcessSuccess(processModel, processType, index)
        resolve(response.data.createLogicoProcess)
      }, function onFailure(error: GraphQLErrorResponse) {
        handleProcessError(processModel, processType, error, index);
        resolve(error)
      })
    })
  }

  const createFlexico = (processModel: ProcessModel, processType:string, index: number): Promise<IDResponse | GraphQLErrorResponse>  => {
    const flexicoData = removeSecondaryDetails(processModel.processData);
    flexicoData[FRISCO_FLEXICO_PROCESS_KEYS.created_by] = userId;
    return new Promise((resolve, _) => {
      createFlexicoTemplate(flexicoData, async function onSuccess(response: CreateFlexicoProcessResponse) {
        createAuditLog(`FLEXICO`, `ADD_REQ#${getCurrentTime()}`, "Adding a template", JSON.stringify({}), JSON.stringify(flexicoData), "ADD REQ", userId);
        if (response.data.createFlexicoTemplate.flex_id) {
          const secondaryDetailData = getSecondaryDetailData(processModel.processData, processType, response.data.createFlexicoTemplate.flex_id, response.data.createFlexicoTemplate.mapping_id);
          await addSecondaryDetails(secondaryDetailData, processType, userId, currentStage);
        }
        handleProcessSuccess(processModel, processType, index);
        resolve(response.data.createFlexicoTemplate)
      }, function onFailure(error: GraphQLErrorResponse) {
        handleProcessError(processModel, processType, error, index);
        resolve(error)
      })
    })
  }

  const createFrisco = (processModel: ProcessModel, processType:string, index: number): Promise<IDResponse | GraphQLErrorResponse> => {
    const friscoData = removeSecondaryDetails(processModel.processData);
    friscoData[FRISCO_FLEXICO_PROCESS_KEYS.created_by] = userId;
    return new Promise((resolve, _) => {
      createFriscoProcess(friscoData, async function onSuccess(response: CreateFriscoProcessResponse) {
        createAuditLog(`FRISCO`,`ADD_REQ#${getCurrentTime()}`, "Adding a process", JSON.stringify({}), JSON.stringify(friscoData), "ADD REQ", userId)
        if (response.data.createProcessV2.process_id) {
          const secondaryDetailData = getSecondaryDetailData(processModel.processData, processType, response.data.createProcessV2.process_id, response.data.createProcessV2.mapping_id);
          await addSecondaryDetails(secondaryDetailData, processType, userId, currentStage);
        }
        handleProcessSuccess(processModel, processType, index)
        resolve(response.data.createProcessV2)
      }, function onFailure(error: GraphQLErrorResponse) {
        handleProcessError(processModel, processType, error, index);
        resolve(error)
      })
    })
  }

  const initUploadModelUpload = (uploadModel: UploadModel) => {
    uploadModel.creating = true;

    if (!uploadModel.app.created) {
      uploadModel.app.creating = true;
      uploadModel.app.error = undefined;
    }

    Object.keys(uploadModel.processes).forEach((processType) => {
      uploadModel.processes[processType].forEach((process) => {
        if (!process.created) {
          process.creating = true;
          process.error = undefined;
        }
      })
    });
    setUploadModel(structuredClone(uploadModel));
  }

  const uploadApp = async (uploadModelLocal: UploadModel) => {
    initUploadModelUpload(uploadModelLocal)
    let appId: string | undefined;
    try {
      appId = await createApp(uploadModelLocal.app)
    } catch (e) {
      console.error(e);
    }
    if (appId) {
      let createProcessPromises: Promise<any>[] = []
      Object.keys(uploadModel.processes).forEach((process: string) => {
        let createProcess: CreateProcess;

        switch (process) {
          case "frisco":
            createProcess = createFrisco
            break;
          case "flexico":
            createProcess = createFlexico
            break;
          case "logico":
            createProcess = createLogico
            break;
          default:
            return;
        }

        createProcessPromises = createProcessPromises.concat(uploadModelLocal.processes[process].map((processModel: ProcessModel, index: number) => {
          processModel.processData && (processModel.processData["app_id"] = appId);
          if (!processModel.created) {
            // including .catch here makes Promise.all wait for all promises to finish running even if one fials
            return createProcess(processModel, process, index).catch(e => { console.error(`'${process}, ${index} failed.'`, e); return e;});
          }
          return new Promise((resolve) => resolve(""));
        }))
      })
      try {
        await Promise.all(createProcessPromises);
      } catch (error) {
        console.error("FAILURE HAPPENED WHILE CREATING PROCESSES", error);
      }
    }
    uploadModel.creating = false;
    setUploadModel(structuredClone(uploadModel));
  }

  const isAppFullyUploaded = (localUploadModel: UploadModel) => {
    if (!localUploadModel.app.created || localUploadModel.creating) return false;
    return !Object.keys(localUploadModel.processes).find(processType => {
      return localUploadModel.processes[processType].find((process: ProcessModel) => !process.created)
    })
  }

  useEffect(() => {
    if (isAppFullyUploaded(uploadModel)) {
      setTimeout(() => {
        // upon successful creation of app, go to app page 
        // TODO: go to app edit page (currently no way to link directly to app edit page)
        uploadModel.app?.appData?.appId && history.push(getAppDetailsUrl(uploadModel.app.appData.appId))
    }, REDIRECT_WAIT_TIME);
    }
  }, [uploadModel])

  return (
    <Modal
      onDismiss={() => {
        setShowModal(false);
        onCloseModal();
      }}
      visible={showModal}
      header={UploadAppModalText.Title}
      footer={
        <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowModal(false)}>{UploadAppModalText.CancelButton}</Button>
                <Button variant="primary" disabled={processingFile || !isAppLoaded() || uploadModel.creating} onClick={() => {
                    isAppLoaded() && uploadApp(uploadModel)
                }}>
                  {(processingFile) && (<Spinner/>)} {UploadAppModalText.UploadButton}
                </Button>
            </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <Box margin={{top:"l", bottom:"l"}}>
        { (!isAppLoaded()) &&
        <Box textAlign="center">
          <FileUpload
            onChange={({ detail }) => setUploadFile(detail.value[0])}
            value={uploadFile ? [uploadFile] : []}
            fileErrors={uploadError}
            i18nStrings={{
              uploadButtonText: _ => UploadAppModalText.ChooseFile,
              errorIconAriaLabel: UploadAppModalText.ErrorAria,
            }}
            constraintText={UploadAppModalText.FileTypeDefinition}
          />
        </Box>
        }
        { 
          (isAppLoaded() && !uploadModel.creating  && !isAppFullyUploaded(uploadModel))  && <UploadAppPreview
              uploadModel={uploadModel}
              currentAppNames={currentAppNames}
              setUploadAppData={setUploadAppData}
              setUploadModel={setUploadModel}
              setUploadFile={setUploadFile}
          />
          
        }
        {
          (uploadModel.creating && !isAppFullyUploaded(uploadModel)) && (
            <UploadAppLoadingScreen uploadModel={uploadModel} />
          )
        }
        {
          isAppFullyUploaded(uploadModel) && (
            <Box margin={{top:"l", bottom:"xxxl"}} textAlign="center">
              <SpaceBetween size="s">
                <Box color="text-label" fontSize="heading-m">
                  {UploadAppModalText.UploadComplete}
                </Box>
                <Box color="text-status-inactive">
                  {UploadAppModalText.RedirectNotice(getAppDetailsUrl(uploadModel?.app?.appData?.appId))}
                </Box>
              </SpaceBetween>
            </Box>
          ) 
        }
      </Box>
    </Modal>
  );
};

export default UploadAppModal;

const renderAppModelName = (uploadModel: UploadModel, currentAppNames: Set<string>, setUploadModel: (model: UploadModel) => void) => {
  const modelName = uploadModel.app.appData?.app_name;

  if (typeof modelName === "string") {
    if (uploadModel.app.created) {
      return (
        <Box>
          {modelName} <Icon name="status-positive" variant="success"/>
        </Box>
      )
    } else {
      return (
        <Box>
          <Input value={modelName} invalid={currentAppNames.has(modelName.trim())} onChange={(e) => {
            uploadModel.app.appData?.app_name && (uploadModel.app.appData.app_name = e.detail.value);
            setUploadModel(structuredClone(uploadModel));
          }} />
        </Box>
      )
    }
  }

  return (<></>)
}

const renderProcessModelName = (uploadModel: UploadModel, processType: string, index: number, setUploadModel: (uploadModel: UploadModel) => void) => {
  const processModel = uploadModel.processes[processType][index];
  const {processName} = PROCESS_TYPE_CONFIG[processType];
  const processErrorMessage = processModel?.error;
  let errorObject;
  if (processErrorMessage) {
    const errorMatcher = Object.keys(ERROR_MATCHERS).find((matcher: string) => processErrorMessage.includes(matcher))
    errorMatcher && (errorObject = ERROR_MATCHERS[errorMatcher]);
  }
  const errorMessage = errorObject ? errorObject.displayError : (processErrorMessage ? processErrorMessage : undefined);
  const errorKey = !!errorObject ? errorObject.input : processName;
  const inputValue = processModel?.processData ? processModel?.processData[errorKey] : "";

  return (
    <Box>
      <Box>
        {processModel.processData[processName]} {errorMessage ? <Icon name="status-negative" variant="error"/> : (processModel.created && <Icon name="status-positive" variant="success"/>)}
      </Box>
      {
        errorMessage && (
          <Box margin={{left:"l"}}>
            <FormField label={errorKey} errorText={errorMessage}>
              <Input value={inputValue ? inputValue : ""} onChange={(e) => {
                const processData = uploadModel.processes[processType][index].processData;
                processData && (processData[errorKey] = e.detail.value);
                setUploadModel(structuredClone(uploadModel));
              }} />
            </FormField>
          </Box>
        )
      }
    </Box>
  )
}

const UploadAppPreview = ({
  uploadModel,
  currentAppNames, 
  setUploadAppData, 
  setUploadModel, 
  setUploadFile,
}: UploadAppPreviewProps) => {
  return (
    <Box>
      <SpaceBetween direction="horizontal" size="xl">
        <Header variant="h3">
          {UploadAppModalText.AppPreviewTitle}
        </Header>
        {
          !uploadModel.app.created && (
            <Button variant="link" onClick={() => {
            setUploadAppData(undefined)
            setUploadFile(undefined)
            }}>
              {UploadAppModalText.ChooseDifferentFile}
            </Button>
          )
        }
      </SpaceBetween>
      <Box margin={{left: "l", bottom:"m"}}>
        <FormField 
          label="App Name:" 
          errorText={hasDuplicateAppName(currentAppNames, uploadModel) 
            ? UploadAppModalText.AppNameExists(uploadModel.app?.appData?.app_name)
            : undefined
          }>
          {renderAppModelName(uploadModel, currentAppNames, setUploadModel)}
        </FormField>
      </Box>
      <Box>          
        <Header variant="h3">{UploadAppModalText.ProcessPreviewTitle}</Header>
        <Box margin={{left:"l"}}>
          <SpaceBetween size="m">
            {Object.keys(uploadModel.processes).map((processType:string, index: number)  => {
              return (
                <Box key={index}>
                  <Header variant="h3">{capitalizeFirstLetter(processType)}</Header>
                  <Box>
                    {uploadModel.processes[processType].length > 0 ? uploadModel.processes[processType].map((process:ProcessModel, index: number) => {
                      return renderProcessModelName(uploadModel, processType, index, setUploadModel)                          
                    }) : (
                      <Box color="text-status-inactive">{UploadAppModalText.NoProcesses}</Box>
                    )}
                  </Box>
                </Box>
              )
            })}
          </SpaceBetween>
        </Box>
      </Box>
    </Box>
  )
}

const renderUploadState = (model: BaseModel) => {
  if (model.creating) {
    return (<Icon name="status-pending"/>)
  } else {
    return model.error ? (<Icon name="status-negative" variant="error"/>) : (<Icon name="status-positive" variant="success"/>)
  }
}

const UploadAppLoadingScreen = ({uploadModel}:UploadAppLoadingScreenProps) => {
  return (
    <Box>
      <SpaceBetween direction="horizontal" size="xl">
        <Header variant="h3">
          {UploadAppModalText.AppUploadProgressTitle}
        </Header>
      </SpaceBetween>

      <Box margin={{left:"l", top: "m", bottom: "m"}}>
        {uploadModel.app.appData ? uploadModel.app.appData.app_name : "App"}: {renderUploadState(uploadModel.app)}
      </Box>

      <Box>          
        <Header variant="h3">{UploadAppModalText.ProcessUploadProgressTitle}</Header>
        <Box margin={{left:"l"}}>
          <SpaceBetween size="m">
            {Object.keys(uploadModel.processes).map((processType:string, index: number)  => {
              return (
                <Box key={index}>
                  <Header variant="h3">{capitalizeFirstLetter(processType)}</Header>
                  <Box margin={{left:"l"}}>
                    {uploadModel.processes[processType].length > 0 ? uploadModel.processes[processType].map((process:ProcessModel, index: number) => {
                      const {processName} = PROCESS_TYPE_CONFIG[processType];
                      const currentProcessName = process?.processData ? process?.processData[processName] : "";
                      return (
                        <Box key={index}>
                          {currentProcessName}: {renderUploadState(process)} 
                        </Box>
                        )
                    }) : (
                      <Box color="text-status-inactive">{UploadAppModalText.NoProcesses}</Box>
                    )}
                  </Box>
                </Box>
              )
            })}
          </SpaceBetween>
        </Box>
      </Box>
    </Box>
  )
}